import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, ScanLine } from 'lucide-react';

interface QRScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanSuccess: (decodedText: string) => void;
}

export const QRScannerDialog: React.FC<QRScannerDialogProps> = ({
  open,
  onOpenChange,
  onScanSuccess,
}) => {
  const scannerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!open) return;

    let html5QrCode: any = null;
    let mounted = true;

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted) return;

        html5QrCode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1,
          },
          (decodedText: string) => {
            // Éxito: detener y devolver resultado
            html5QrCode.stop().catch(() => {});
            scannerRef.current = null;
            // IMPORTANT: notify parent FIRST so data is injected while dialog is still protected
            const result = decodedText;
            onScanSuccess(result);
            // Then close scanner with delay to avoid race condition with parent dialog
            setTimeout(() => onOpenChange(false), 300);
          },
          () => {
            // Escaneo en progreso, sin resultado aún
          }
        );

        if (mounted) {
          setScanning(true);
          setError(null);
        }
      } catch (err: any) {
        if (mounted) {
          console.error('Error al iniciar cámara:', err);
          setError(
            err?.message?.includes('Permission')
              ? 'Permiso de cámara denegado. Activa el acceso a la cámara en los ajustes del navegador.'
              : 'No se pudo acceder a la cámara. Asegúrate de que el dispositivo tiene cámara disponible.'
          );
        }
      }
    };

    // Pequeño delay para que el DOM esté listo
    const timer = setTimeout(startScanner, 300);

    return () => {
      mounted = false;
      clearTimeout(timer);
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
      setScanning(false);
      setError(null);
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[95vw] p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" />
            Escanear código QR
          </DialogTitle>
          <DialogDescription>
            Apunta la cámara al código QR o de barras del equipo.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full" ref={containerRef}>
          <div
            id="qr-reader"
            className="w-full rounded-lg overflow-hidden bg-muted min-h-[280px]"
          />

          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
              <div className="text-center text-muted-foreground">
                <Camera className="h-10 w-10 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Iniciando cámara...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg p-4">
              <div className="text-center">
                <CameraOff className="h-10 w-10 mx-auto mb-3 text-destructive" />
                <p className="text-sm text-destructive font-medium mb-3">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  className="min-h-[44px]"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Compatible con códigos QR y códigos de barras
        </p>
      </DialogContent>
    </Dialog>
  );
};
